import { nameof } from 'ts-simple-nameof'
import * as yup from 'yup'
import { type AdditionalInfoValidationSchemaContext, customNullable } from '.'
import type { VehicleIdentificationDto, VehiclePriceDto } from '../dtos'
import { Selection } from '../enums'
import { isIn } from '../Utils/comparisonUtils'

export const znackaRequiredTypes = [
  Selection.Personal,
  Selection.Utility,
  //Selection.Motorcycle,
  Selection.Offroad,
]

export type znackaRequiredTypes = typeof znackaRequiredTypes[keyof typeof znackaRequiredTypes]

const carUpTo3500kgTypes = [...znackaRequiredTypes]

export const isOtherMake = (make?: string | null) => {
  return (make ?? '').toUpperCase() == 'OSTATNÍ'
}

export const IsTrailer = (category?: string | null) =>
  category && isIn(parseInt(category), Selection.Trailer, Selection.SemiTrailer, Selection.CamperTrailer)

export const IsTruck = (category?: string | null) =>
  category && Selection.TruckOver35t == parseInt(category)

export const IsTractor = (category?: string | null) =>
  category && isIn(parseInt(category), Selection.TractorWithRegistrationMark, Selection.TractorWithoutRegistrationMark)

export const IsTahac = (category?: string | null) =>
  category && Selection.Tahac == parseInt(category)

export const IsWorkMachine = (category?: string | null) =>
  category && isIn(parseInt(category), Selection.WorkMachine, Selection.WorkMachineNoRegistration)

export const IsCamperVehicle = (category?: string | null) =>
  category && Selection.CamperVehicle == parseInt(category)

export const IsBus = (category?: string | null) =>
  category && isIn(parseInt(category), Selection.Bus, Selection.PublicBus)

export const IsOtherNonregistered = (category?: string | null) =>
  category && Selection.OtherNonregistered == parseInt(category)

export const IsVolumeFuel = (fuel?: string | null) =>
  !fuel || Selection.Electricity != parseInt(fuel)

export const IsSubaruLessThan3YearsOld = (make?: string | null, firstRegDate?: Date | null, isInProduction?: boolean | null) => {
  if (make && make.toUpperCase() == 'SUBARU') {
    if (isInProduction)
      return true
    if (!firstRegDate)
      return false
    const regDate = (typeof (firstRegDate) === 'string') ? new Date(firstRegDate) : firstRegDate
    const date = new Date()
    date.setFullYear(date.getFullYear() - 3)
    return regDate >= date
  }
  return false
}

export const IsMazdaLessThan3YearsOld = (make?: string | null, firstRegDate?: Date | null, isInProduction?: boolean | null) => {
  if (make && make.toUpperCase() == 'MAZDA') {
    if (isInProduction)
      return true
    if (!firstRegDate)
      return false
    const regDate = (typeof (firstRegDate) === 'string') ? new Date(firstRegDate) : firstRegDate
    const date = new Date()
    date.setFullYear(date.getFullYear() - 3)
    return regDate >= date
  }
  return false
}

export const IsKiaLessThan2YearsOld = (make?: string | null, firstRegDate?: Date | null, isInProduction?: boolean | null) => {
  if (make && make.toUpperCase() == 'KIA') {
    if (isInProduction)
      return true
    if (!firstRegDate)
      return false
    const regDate = (typeof (firstRegDate) === 'string') ? new Date(firstRegDate) : firstRegDate
    const date = new Date()
    date.setFullYear(date.getFullYear() - 2)
    return regDate >= date
  }
  return false
}

export const IsMgLessThan3YearsOld = (make?: string | null, firstRegDate?: Date | null, isInProduction?: boolean | null) => {
  if (make && make.toUpperCase() == 'MG') {
    if (isInProduction)
      return true
    if (!firstRegDate)
      return false
    const regDate = (typeof (firstRegDate) === 'string') ? new Date(firstRegDate) : firstRegDate
    const date = new Date()
    date.setFullYear(date.getFullYear() - 3)
    return regDate >= date
  }
  return false
}

export const VehicleValidationRuleObject = {
  licensePlate: yup
    .string()
    .nullable()
    .max(8, 'SPZ může mít maximálně 8 znaků')
    .when('licensePlate', {
      is: (spz?: string) => (spz?.length ?? 0) > 0,
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]*$/, 'SPZ nesmí obsahovat nepovolené znaky'),
      otherwise: undefined,
    }),

  cisloTechnickehoPrukazu: yup
    .string()
    .nullable()
    .when('cisloTechnickehoPrukazu', {
      is: (tp?: string) => (tp?.length ?? 0) > 0,
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]+$/, 'Číslo TP nesmí obsahovat nepovolené znaky'),
      otherwise: undefined,
    }),

  vin: yup
    .string()
    .nullable()
    .when(
      [
        nameof<VehicleIdentificationDto>((x) => x.vin),
        nameof<VehicleIdentificationDto>((x) => x.licensePlate),
        nameof<VehiclePriceDto>((x) => x.useVehicleEvaluation),
      ],
      {
        is: (
          vin?: string,
          licensePlate?: string,
          useVehicleEvaluation?: boolean
        ) =>
          (vin?.length ?? 0) == 0 &&
          (licensePlate?.length ?? 0) == 0 &&
          useVehicleEvaluation == true,
        then: () => yup
          .string()
          .required(
            'Jedno z polí VIN nebo Reg. značka (SPZ) musí být vyplněno'
          ),
        otherwise: undefined,
      }
    )
    .when(['vin', 'druh'], {
      is: (vin?: string, druh?: number) => (vin?.length ?? 0) > 0 && !IsOtherNonregistered(druh?.toString()),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]*$/, 'VIN nesmí obsahovat nepovolené znaky'),
      otherwise: undefined,
    }),

  druh: yup.number().required('Vyberte druh vozidla'),

  pocetMistSezeni: customNullable(yup.number())
    .when('druh', {
      is: (druh?: number) => !IsTrailer(druh?.toString()) && !IsOtherNonregistered(druh?.toString()),
      then: () => customNullable(yup.number()).min(1, 'Počet míst k sezení musí být větší než 0').required('Zadejte počet míst k sezení'),
      otherwise: () => customNullable(yup.number()),
    }),

  palivo: customNullable(yup.number())
    .when('druh', {
      is: (druh?: number) => !IsTrailer(druh?.toString()),
      then: () => customNullable(yup.number()).required('Vyberte palivo'),
      otherwise: undefined,
    }),

  // znacka je povinna pro vsechny druhy vozidla - minimalne musi byt vybrano OSTATNÍ
  znacka: yup
    .string()
    .nullable()
    .when('druh', {
      is: (druh?: number) => !!druh,
      then: () => yup
        .string()
        .required('Vyberte značku vozidla'),
      otherwise: undefined,
    }),

  // znackaText (tovarni znacka textem) je povinny jen kdyz je vybrana znacka OSTATNÍ a zaroven plati stavajici podminky
  znackaText: yup
    .string()
    .nullable()
    .when(['znacka', 'druh'], {
      is: (znacka?: string, druh?: number) =>
        isOtherMake(znacka) &&
        druh && !isIn(parseInt(druh.toString()), ...znackaRequiredTypes),
      then: () => yup
        .string()
        .required('Zadejte značku vozidla')
        .matches(/^[aA-zZ0-9čČšŠěĚëË\s\-&]+$/, 'Zadejte značku vozidla'),
      otherwise: undefined,
    }),

  model: yup
    .string()
    .when('brokerMakeModelId', {
      is: (brokerMakeModelId?: number) => !brokerMakeModelId,
      then: () => yup
        .string()
        .required('Zadejte model'),
      otherwise: undefined,
    }),

  objem: customNullable(yup.number())
    .when(['druh', 'palivo'], {
      is: (druh?: number, palivo?: number) =>
        !IsTrailer(druh?.toString()) && !IsOtherNonregistered(druh?.toString()) && IsVolumeFuel(palivo?.toString()),
      then: () => customNullable(yup.number()).required('Zadejte objem'),
      otherwise: undefined,
    }),

  vykon1: customNullable(yup.number()).when('druh', {
    is: (druh?: number) => !IsTrailer(druh?.toString()),
    then: () => customNullable(yup.number())
      .min(1, 'Hodnota výkonu musí být nejméně 1 kW')
      .required('Zadejte výkon'),
    otherwise: undefined,
  }),

  celkovaHmotnost: customNullable(yup.number())
    .when('druh', {
      is: (druh?: number | null) =>
        !IsTrailer(druh?.toString()) &&
        !IsTruck(druh?.toString()) &&
        !IsTractor(druh?.toString()) &&
        !IsTahac(druh?.toString()) &&
        !IsWorkMachine(druh?.toString()) &&
        !IsCamperVehicle(druh?.toString()) &&
        !IsBus(druh?.toString()),
      then: () => customNullable(yup.number())
        .required('Zadejte celkovou hmotnost')
        .max(3500, 'Celková hmotnost vozidla musí být maximálně 3 500 kg'),
      otherwise: () => customNullable(yup.number())
        .required('Zadejte celkovou hmotnost'),
    }),

  usage: yup.string().required('Vyberte způsob použití'),

  firstRegistrationDate: yup.string().when('isInProduction', {
    is: (isInProduction?: boolean | null) => !isInProduction,
    then: schema => schema.required('Pole datum první registrace je povinné'),
    otherwise: schema => schema.nullable(),
  }),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === '' ? null : currentValue
    })
    .when(['druh', 'doNotValidateMileage'], {
      is: (druh?: number, doNotValidateMileage?: boolean) =>
        !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && !doNotValidateMileage,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          'Pole stav tachometru je povinné'
        ),
      otherwise: undefined,
    }),

  warrantyKmLimit: yup.string()
    .when(
      'manufacturerWarrantyType',
      {
        is: (manufacturerWarrantyType?: number) =>
          manufacturerWarrantyType == 1,
        then: () => yup.string().required('Vyberte počet km záruky'),
        otherwise: undefined,
      }
    ),

  warrantyTermLimit: yup.string().when(
    'manufacturerWarrantyType',
    {
      is: (manufacturerWarrantyType?: number) =>
        manufacturerWarrantyType == 1,
      then: () => yup.string().required(
        'Vyberte počet měsíců záruky'
      ),
      otherwise: undefined,
    }
  ),

  extraEquipments: yup.array().of(
    yup.object().shape({
      name: yup.string().required('Zadejte popis mimořádné výbavy'),
      price: customNullable(yup.number()).required(
        'Zadejte hodnotu mimořádné výbavy'
      )
    })
  )
}

export const VehiclePublicPoleasValidationRuleObject = {
  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === '' ? null : currentValue
    })
    .when(['druh', 'doNotValidateMileage'], {
      is: (druh?: number, doNotValidateMileage?: boolean) =>
        !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && !doNotValidateMileage,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          'Pole stav tachometru je povinné'
        ),
      otherwise: undefined,
    }),

  manufacturerWarrantyType: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? NaN : Number(originalValue)
    )
    .oneOf([0, 1, 2], 'Vyberte, zda je vozdilo v záruce')
    .required('Uveďte, zda je vozdilo v záruce'),

  warrantyKmLimit: yup.string()
    .when(
      'manufacturerWarrantyType',
      {
        is: (manufacturerWarrantyType?: number) =>
          manufacturerWarrantyType == 1,
        then: () => yup.string().required('Vyberte počet km záruky'),
        otherwise: undefined,
      }
    ),

  warrantyTermLimit: yup.string().when(
    'manufacturerWarrantyType',
    {
      is: (manufacturerWarrantyType?: number) =>
        manufacturerWarrantyType == 1,
      then: () => yup.string().required(
        'Vyberte počet měsíců záruky'
      ),
      otherwise: undefined,
    }
  ),
  yearKm: yup
    .string()
    .transform((_, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === undefined ||
        originalValue === null
      )
        return null
      return String(originalValue)
    })
    .nullable()
    .when('druh', ([druh], schema: yup.StringSchema<string | null | undefined>) => {
      const isUpTo3500 = carUpTo3500kgTypes.includes(Number(druh) as any)

      return isUpTo3500
        ? schema.required('Vyberte roční nájezd km')
        : schema.notRequired()
    })
}

export const AddVehicleInfoValidationRuleObject = {
  firstRegistrationDate: customNullable(yup.string()
    .when(['priceType', 'useVehicleEvaluation'], {
      is: (priceType?: number, useVehicleEvaluation?: boolean) => priceType == Selection.VehicleValuation && useVehicleEvaluation == true,
      then: () =>
        yup.string()
          .required('Pole datum první registrace je povinné'),
      otherwise: () => yup.string(),
    })),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === '' ? null : currentValue
    })
    .when(['druh', 'priceType', 'useVehicleEvaluation'], {
      is: (druh?: number, priceType?: number, useVehicleEvaluation?: boolean) => !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && priceType == Selection.VehicleValuation && useVehicleEvaluation == true,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          'Pole stav tachometru je povinné'
        ),
      otherwise: undefined,
    })
}

export const DenniPovVehicleValidationRuleObject = {
  vin: yup
    .string()
    .when(
      [
        nameof<VehicleIdentificationDto>((x) => x.vin),
        nameof<VehicleIdentificationDto>((x) => x.licensePlate),
        nameof<VehiclePriceDto>((x) => x.useVehicleEvaluation),
      ],
      {
        is: (
          vin?: string,
          licensePlate?: string,
          useVehicleEvaluation?: boolean
        ) =>
          (vin?.length ?? 0) == 0 &&
          (licensePlate?.length ?? 0) == 0 &&
          useVehicleEvaluation == true,
        then: () => yup
          .string()
          .required(
            'Jedno z polí VIN nebo Reg. značka (SPZ) musí být vyplněno'
          ),
        otherwise: undefined,
      }
    )
    .when(['vin', 'druh'], {
      is: (vin?: string, druh?: number) => (vin?.length ?? 0) > 0 && !IsOtherNonregistered(druh?.toString()),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]*$/, 'VIN nesmí obsahovat nepovolené znaky'),
      otherwise: undefined,
    }),

  druh: yup.number().required('Vyberte druh vozidla'),

  palivo: customNullable(yup.number())
    .when('druh', {
      is: (druh?: number) => !IsTrailer(druh?.toString()),
      then: () => customNullable(yup.number()).required('Vyberte palivo'),
      otherwise: undefined,
    }),

  znacka: yup
    .string()
    .nullable()
    .when('druh', {
      is: (druh?: number) =>
        druh && isIn(parseInt(druh.toString()), ...znackaRequiredTypes),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9čČšŠěĚëË\s\-&]+$/, 'Vyberte značku vozidla'),
      otherwise: undefined,
    }),
  model: yup.string().required('Zadejte model'),

  objem: customNullable(yup.number()).when(['druh', 'palivo'], {
    is: (druh?: number, palivo?: number) =>
      !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && IsVolumeFuel(palivo?.toString()),
    then: () => customNullable(yup.number()).required('Zadejte objem'),
    otherwise: undefined,
  }),

  vykon1: customNullable(yup.number()).when('druh', {
    is: (druh?: number) => !IsTrailer(druh?.toString()),
    then: () => customNullable(yup.number()).required('Zadejte výkon'),
    otherwise: undefined,
  }),

  celkovaHmotnost: customNullable(yup.number())
    .when('druh', {
      is: (druh?: number | null) =>
        !IsTrailer(druh?.toString()) &&
        !IsTruck(druh?.toString()) &&
        !IsTractor(druh?.toString()) &&
        !IsTahac(druh?.toString()) &&
        !IsWorkMachine(druh?.toString()) &&
        !IsCamperVehicle(druh?.toString()) &&
        !IsBus(druh?.toString()),
      then: () => customNullable(yup.number())
        .required('Zadejte celkovou hmotnost')
        .max(3500, 'Celková hmotnost vozidla musí být maximálně 3 500 kg'),
      otherwise: () => customNullable(yup.number())
        .required('Zadejte celkovou hmotnost'),
    }),

  firstRegistrationDate: yup.string().when('isInProduction', {
    is: (isInProduction?: boolean | null) => !isInProduction,
    then: schema => schema.required('Pole datum první registrace je povinné'),
    otherwise: schema => schema.nullable(),
  }),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === '' ? null : currentValue
    })
    .when(['druh', 'doNotValidateMileage'], {
      is: (druh?: number, doNotValidateMileage?: boolean) =>
        !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && !doNotValidateMileage,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          'Pole stav tachometru je povinné'
        ),
      otherwise: undefined,
    }),
}


// zalezi na poradi - v tomto poradi se pak mezi errory preklikava
export const VehiclePublicPoleasValidationSchema = yup.object().shape(VehiclePublicPoleasValidationRuleObject)
export const VehicleValidationSchema = yup.object().shape(
  VehicleValidationRuleObject,
  [
    // osetreni cyclic dependency vznikajici pri pouziti when... is... then...
    ['vin', 'vin'],
    ['licensePlate', 'licensePlate'],
    ['cisloTechnickehoPrukazu', 'cisloTechnickehoPrukazu']
  ]
) as any

// zalezi na poradi - v tomto poradi se pak mezi errory preklikava
export const DenniPovVehicleValidationSchema = yup.object().shape(
  DenniPovVehicleValidationRuleObject,
  [
    ['vin', 'vin'],
    ['licensePlate', 'licensePlate'],
    ['cisloTechnickehoPrukazu', 'cisloTechnickehoPrukazu']
  ]
) as any

export const AdditionalInfoVehicleValidationSchema = (context?: AdditionalInfoValidationSchemaContext) => yup.object().shape(
  {
    vin: VehicleValidationRuleObject.vin,
    licensePlate: VehicleValidationRuleObject.licensePlate,
    // cisloOrv or cisloTechnickehoPrukazu is required when licensePlate is not empty
    cisloTechnickehoPrukazu: VehicleValidationRuleObject.cisloTechnickehoPrukazu
      .when(['licensePlate', 'cisloOrv'], {
        is: (licensePlate?: string, cisloOrv?: string) => (licensePlate?.length ?? 0) > 0 && (cisloOrv?.length ?? 0) < 1,
        then: () => context?.extra ? yup.string() : yup.string().required('Číslo ORV nebo Číslo TP je povinné'),
        otherwise: undefined,
      }),
    cisloOrv: yup
      .string()
      .nullable()
      .matches(/^[aA-zZ0-9\s]*$/, 'Číslo ORV nesmí obsahovat nepovolené znaky')
      .when(['licensePlate', 'cisloTechnickehoPrukazu'], {
        is: (licensePlate?: string, cisloTechnickehoPrukazu?: string) => (licensePlate?.length ?? 0) > 0 && (cisloTechnickehoPrukazu?.length ?? 0) < 1,
        then: () => context?.extra ? yup.string() : yup.string().required('Číslo ORV nebo Číslo TP je povinné'),
        otherwise: undefined,
      }),
  },
  [
    // osetreni cyclic dependency vznikajici pri pouziti when... is... then...
    ['vin', 'vin'],
    ['licensePlate', 'licensePlate'],
    ['cisloTechnickehoPrukazu', 'cisloTechnickehoPrukazu'],
    ['cisloOrv', 'cisloOrv'],
    ['cisloOrv', 'cisloTechnickehoPrukazu'],
    ['cisloTechnickehoPrukazu', 'cisloOrv'],
  ]
) as any